/**
 * useStoreyStore — building levels (storeys) and the storey view state.
 *
 * "Storey" = a building level. "Floor" = the pavement surface of a storey.
 * Never use "floor" for a level here.
 *
 * Storeys are DERIVED AT SCENE LOAD, never persisted — see STOREY_VIEW_PLAN.md
 * §4. They are a function of the geometry, so a saved list would silently stop
 * matching the model the moment anyone moves a slab or edits the floorplan, and
 * invalidating it would cost more than re-deriving it.
 *
 * Deliberately separate from useCameraStore: storeys are scene structure (they
 * change when the model changes), camera mode is view state. The A-Frame
 * version mixed them, which is how camera-system ↔ storey-manager ↔
 * cross-section became a three-way tangle.
 *
 * Note there is no `activeStoreyForY(cameraY)`: nothing tracks which storey the
 * camera is standing in, because only modelview cuts and there the camera is
 * outside the building. That lookup was the entry point for the old
 * `updateCurrentStorey` mess.
 */

import { create } from 'zustand';

import type { ArchitectureRole } from '../utils/architectureRole';

export type StoreySource = 'floorplan' | 'detected' | 'user';

export interface Storey {
  /** Stable within a session — survives reorder/rename. */
  id: string;
  /** 0 = lowest storey in the scene. Recomputed on any list mutation. */
  index: number;
  /** Producer-supplied name (e.g. a floorplan floor name). Not shown anywhere
   *  today — the toolbar labels storeys by index; see storeyBounds.storeyLabel. */
  name: string;
  /** World Y of the finished floor level (the walkable surface). */
  elevation: number;
  /** Distance to the next storey's elevation; DEFAULT_STOREY_HEIGHT for the topmost. */
  height: number;
  /**
   * 'user' suppresses name regeneration. Nothing sets it today — v1 ships no
   * storey editor (STOREY_VIEW_PLAN.md §8); it exists so an override layer can
   * land later without a type change.
   */
  source: StoreySource;
}

/**
 * 'all'  — nothing cut (the default, and the only possible state outside modelview)
 * 'peel' — active storey + everything below it
 */
export type StoreyViewMode = 'all' | 'peel';

interface StoreyState {
  storeys: Storey[];
  activeStoreyId: string | null;
  viewMode: StoreyViewMode;
  /**
   * objectId → storey index, maintained by useStoreyInit.
   * A plain object rather than a Map so zustand equality checks stay cheap and
   * consumers can destructure it without cloning.
   */
  assignment: Record<string, number>;
  /**
   * objectId → architectural role, from the same pass that builds `assignment`.
   * Kept here so the visibility rule that hides the active storey's ceiling can
   * be applied identically by the renderer and by the click handler, without
   * either of them re-deriving roles from params.
   */
  roles: Record<string, ArchitectureRole>;
  /**
   * Highest world Y over all measured scene objects; null until the first
   * derivation pass has run.
   *
   * Here rather than derived on demand because the only cheap source of it is
   * the AABB pass useStoreyInit already runs, and the one consumer — the
   * single-storey ceiling cut's plausibility gate (storeyBounds
   * .isSingleStoreyCeilingCut) — has to answer synchronously in a selector.
   */
  sceneTopY: number | null;
  /**
   * Has the single-storey ceiling cut already engaged itself for this scene?
   *
   * The latch behind "on by default, once" (VRContext's auto-engage effect).
   * It lives in the store rather than in a component ref because a ref is per
   * component instance, and the Canvas remounts on things as ordinary as a
   * materials change — which would re-arm the auto-engage and switch the cut
   * back on after the user had deliberately turned it off. Cleared by reset()
   * on a real scene switch.
   */
  autoCeilingCutDone: boolean;

  setStoreys(storeys: Storey[]): void;
  setActiveStorey(id: string): void;
  /** No-op at the top — never wraps. Returns true if the active storey changed. */
  storeyUp(): boolean;
  /** No-op at the bottom — never wraps. Returns true if the active storey changed. */
  storeyDown(): boolean;
  setViewMode(mode: StoreyViewMode): void;
  setAssignment(
    assignment: Record<string, number>,
    roles: Record<string, ArchitectureRole>,
  ): void;
  setSceneTopY(y: number | null): void;
  markAutoCeilingCutDone(): void;
  /** Back to first-load state. Called on a genuine scene switch — see
   *  utils/resetSceneState.ts. */
  reset(): void;

  // ─── Derived reads (no subscription) ──────────────────────────────────────
  getActiveStorey(): Storey | null;
  getActiveIndex(): number;
  /**
   * True when there is more than one storey to step between. NOT the same as
   * "the storey cut is available" — a single-storey scene can still offer the
   * cut as a ceiling toggle; see storeyBounds.storeyCutAvailable.
   */
  hasMultipleStoreys(): boolean;
}

/** Everything reset() puts back. Named so the initial state and the reset can
 *  never drift apart. */
const INITIAL = {
  storeys: [] as Storey[],
  activeStoreyId: null as string | null,
  viewMode: 'all' as StoreyViewMode,
  assignment: {} as Record<string, number>,
  roles: {} as Record<string, ArchitectureRole>,
  sceneTopY: null as number | null,
  autoCeilingCutDone: false,
};

export const useStoreyStore = create<StoreyState>((set, get) => ({
  ...INITIAL,

  setStoreys(storeys) {
    // Keep the active storey if it survived the update (same id), otherwise
    // default to the LOWEST storey (index 0).
    //
    // Not the topmost: peeling at the top storey cuts away nothing (there is
    // nothing above it), so the user's first click on the storey-view toggle
    // would appear to do nothing at all. Starting at the bottom opens the
    // building immediately, which is the point of the feature.
    const prevId = get().activeStoreyId;
    const stillThere = storeys.some((s) => s.id === prevId);
    set({
      storeys,
      activeStoreyId: stillThere ? prevId : storeys[0]?.id ?? null,
    });
  },

  setActiveStorey(id) {
    if (!get().storeys.some((s) => s.id === id)) return;
    set({ activeStoreyId: id });
  },

  storeyUp() {
    const { storeys, activeStoreyId } = get();
    const i = storeys.findIndex((s) => s.id === activeStoreyId);
    if (i < 0 || i >= storeys.length - 1) return false;
    set({ activeStoreyId: storeys[i + 1].id });
    return true;
  },

  storeyDown() {
    const { storeys, activeStoreyId } = get();
    const i = storeys.findIndex((s) => s.id === activeStoreyId);
    if (i <= 0) return false;
    set({ activeStoreyId: storeys[i - 1].id });
    return true;
  },

  setViewMode(viewMode) {
    set({ viewMode });
  },

  setAssignment(assignment, roles) {
    set({ assignment, roles });
  },

  setSceneTopY(y) {
    // Guarded: this is recomputed on every settle, and an unchanged write would
    // re-render every storey-cut consumer for nothing.
    if (get().sceneTopY === y) return;
    set({ sceneTopY: y });
  },

  markAutoCeilingCutDone() {
    if (get().autoCeilingCutDone) return;
    set({ autoCeilingCutDone: true });
  },

  reset() {
    set({ ...INITIAL });
  },

  getActiveStorey() {
    const { storeys, activeStoreyId } = get();
    return storeys.find((s) => s.id === activeStoreyId) ?? null;
  },

  getActiveIndex() {
    const { storeys, activeStoreyId } = get();
    const i = storeys.findIndex((s) => s.id === activeStoreyId);
    return i < 0 ? storeys.length - 1 : i;
  },

  hasMultipleStoreys() {
    return get().storeys.length > 1;
  },
}));
